import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  Promotion, PromotionInput,
  listPromotions, createPromotion, updatePromotion, deletePromotion,
} from '../services/promotionsApi';

interface PromotionsState {
  items: Promotion[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchList: () => Promise<void>;
  create: (input: PromotionInput) => Promise<Promotion>;
  update: (id: number, input: Partial<PromotionInput>) => Promise<Promotion>;
  remove: (id: number) => Promise<void>;
}

const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const usePromotionsStore = create<PromotionsState>()(
  devtools(
    (set, get) => ({
      items: [],
      loading: false,
      saving: false,
      error: null,
      fetchList: async () => {
        set({ loading: true, error: null });
        try {
          const data = await listPromotions();
          set({ items: data, loading: false });
        } catch (err) {
          set({ loading: false, error: toErr(err) });
        }
      },
      create: async (input) => {
        set({ saving: true, error: null });
        try {
          const created = await createPromotion(input);
          set((s) => ({ items: [created, ...s.items], saving: false }));
          return created;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      update: async (id, input) => {
        set({ saving: true, error: null });
        try {
          const updated = await updatePromotion(id, input);
          set((s) => ({
            items: s.items.map((it) => (it.id === id ? updated : it)),
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      remove: async (id) => {
        set({ saving: true, error: null });
        try {
          await deletePromotion(id);
          set((s) => ({ items: s.items.filter((it) => it.id !== id), saving: false }));
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
    }),
    { name: 'promotions-store' }
  )
);
